/** Driver race history — all results across seasons, grouped by event */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { rpc, RpcError } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { Pagination } from '@/components/common/Pagination';
import { FilterBar } from '@shared/search';
import { driverHistorySchema } from './driverHistorySchema';
import { PositionBadge } from './PositionBadge';
import { StatusBadge } from './StatusBadge';
import { formatLapTime } from './ResultsTable';
import { SessionTypeBadge } from './SessionTypeBadge';
import { DriverView } from './DriverView';
import { ChevronRight } from 'lucide-react';
import type { DriverRaceResult, GetDriverRaceHistoryResponse } from '@/types/telemetry';

function getWeatherEmoji(condition: string): string {
  const c = condition.toLowerCase();
  if (c.includes('thunder')) return '⛈️';
  if (c.includes('snow')) return '❄️';
  if (c.includes('rain') || c.includes('shower')) return '🌧️';
  if (c.includes('drizzle')) return '🌦️';
  if (c.includes('fog') || c.includes('mist')) return '🌫️';
  if (c.includes('cloud') || c.includes('overcast')) return '☁️';
  if (c.includes('clear') || c.includes('sun')) return '☀️';
  return '☀️';
}

interface DriverHistoryProps {
  driverId: string;
}

interface EventGroup {
  key: string;
  dateStart: string;
  trackName: string;
  year: number;
  championshipName: string;
  results: DriverRaceResult[];
}

function getHashParams(): URLSearchParams {
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  return new URLSearchParams(qIdx >= 0 ? hash.slice(qIdx) : '');
}

function updateHashParams(page: number, query: string) {
  const hash = window.location.hash;
  const base = hash.includes('?') ? hash.slice(0, hash.indexOf('?')) : hash;
  const sp = new URLSearchParams();
  if (page > 1) sp.set('page', String(page));
  if (query) sp.set('q', query);
  const qs = sp.toString();
  window.history.replaceState(null, '', qs ? `${base}?${qs}` : base);
}

function groupByEvent(results: DriverRaceResult[]): EventGroup[] {
  const map = new Map<string, EventGroup>();
  for (const r of results) {
    const key = `${r.dateStart}::${r.trackName}`;
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        dateStart: r.dateStart,
        trackName: r.trackName,
        year: r.year,
        championshipName: r.championshipName,
        results: [],
      };
      map.set(key, group);
    }
    group.results.push(r);
  }
  return Array.from(map.values());
}

const PAGE_SIZE = 10;

/* Grid templates */
const EVENT_GRID = 'grid-cols-[24px_110px_1fr_60px_100px]';
const SESSION_GRID = 'grid-cols-[24px_1fr_100px_60px_60px_80px_70px]';

export function DriverHistory({ driverId }: DriverHistoryProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();

  const [data, setData] = useState<GetDriverRaceHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [page, setPage] = useState(() => {
    const p = parseInt(getHashParams().get('page') || '1');
    return p > 0 ? p : 1;
  });
  const [query, setQuery] = useState(() => getHashParams().get('q') || '');
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Expand/collapse state (set of event keys)
  const [expanded, setExpanded] = useState<Set<string>>(new Set());


  const loadData = useCallback(async (p: number, q: string) => {
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { driver_id: driverId, page: p, page_size: PAGE_SIZE };
      if (q) body.query = q;
      const res = await rpc<GetDriverRaceHistoryResponse>('GetDriverRaceHistory', body);
      setData(res);
      setTotalCount(res.totalCount ?? 0);
      setTotalPages(Math.max(1, Math.ceil((res.totalCount ?? 0) / (res.pageSize ?? PAGE_SIZE))));
    } catch (e: any) {
      const nf = e instanceof RpcError && e.isNotFound;
      setNotFound(nf);
      setError(nf ? 'The requested driver could not be found.' : (e.message || t('races.failedToLoad')));
    } finally {
      setLoading(false);
    }
  }, [driverId, t]);

  useEffect(() => { loadData(page, query); }, [page, query, loadData]);

  useEffect(() => {
    updateHashParams(page, query);
  }, [page, query]);

  // Reset expanded state when page/query changes
  useEffect(() => {
    setExpanded(new Set());
  }, [page, query]);

  const eventGroups = useMemo(() => groupByEvent(data?.results ?? []), [data]);

  const toggleEvent = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSearch = (newQuery: string) => {
    setPage(1);
    setQuery(newQuery);
  };

  if (loading && !data) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} isNotFound={notFound} />;
  if (!data) return <ErrorState message={t('races.noData')} />;

  return (
    <>
      <DriverView driverId={driverId} activeTab="races" />

      {/* Filter Toolbar */}
      <div className="mb-6">
        <FilterBar
          schema={driverHistorySchema}
          initialQuery={query}
          onSearch={handleSearch}
          enableFilterUI={true}
        />
      </div>

      {/* Results — event-grouped table */}
      <h2 className="section-title mb-4">{t('races.results')}</h2>

      {eventGroups.length === 0 ? (
        <div className="card p-8 text-center text-text-muted text-sm">{t('races.noData')}</div>
      ) : (
        <>
          <div className="card p-0 overflow-hidden">
            {/* Event-level header (desktop) */}
            <div className={`hidden sm:grid ${EVENT_GRID} gap-3 px-4 py-2.5 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted border-b border-border-subtle`}>
              <span></span>
              <span>{t('sessionList.date')}</span>
              <span>{t('races.track')}</span>
              <span>{t('races.year')}</span>
              <span>{t('races.session')}</span>
            </div>

            {eventGroups.map((group, gi) => {
              const isExpanded = expanded.has(group.key);
              const sessionCount = group.results.length;
              const sessionCountLabel = sessionCount === 1
                ? t('races.sessionCountSingular')
                : t('races.sessionCount', { count: sessionCount });

              return (
                <div key={group.key}>
                  {/* Event parent row (desktop) */}
                  <div
                    onClick={() => toggleEvent(group.key)}
                    className={`hidden sm:grid ${EVENT_GRID} gap-3 px-4 py-3.5
                      cursor-pointer hover:bg-bg-hover active:scale-[0.998]
                      transition-all select-none items-center
                      ${!isExpanded && gi < eventGroups.length - 1 ? 'border-b border-border-subtle/50' : ''}`}
                  >
                    <ChevronRight
                      size={14}
                      className={`text-text-muted transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                    />
                    <span className="text-text-secondary text-xs tabular-nums font-mono">
                      {group.dateStart}
                    </span>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold text-text-primary text-sm truncate">{group.trackName}</span>
                      {group.championshipName && (
                        <span className="text-text-muted text-xs truncate">· {group.championshipName}</span>
                      )}
                    </div>
                    <span>
                      <span className="inline-block px-2 py-0.5 bg-white/[0.06] border border-border-subtle rounded-full text-[0.7rem] font-medium text-text-secondary tabular-nums">
                        {group.year}
                      </span>
                    </span>
                    <span className="text-text-muted text-xs">
                      {sessionCountLabel}
                    </span>
                  </div>

                  {/* Expanded: session sub-header + session rows (desktop) */}
                  {isExpanded && (
                    <>
                      {/* Session column header */}
                      <div className={`hidden sm:grid ${SESSION_GRID} gap-3 px-4 py-2 bg-bg-surface/50
                        text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted border-t border-border-subtle`}
                      >
                        <span></span>
                        <span>{t('races.session')}</span>
                        <span>{t('races.category')}</span>
                        <span className="text-center">{t('races.finish')}</span>
                        <span className="text-right">{t('races.bestLap')}</span>
                        <span className="text-right">{t('sessionList.weather')}</span>
                        <span className="text-center">{t('races.status')}</span>
                      </div>

                      {/* Session rows */}
                      {group.results.map((r, si) => {
                        const icon = r.weatherCondition ? getWeatherEmoji(r.weatherCondition) : null;
                        const wTemp = r.weatherTemperatureC != null && r.weatherTemperatureC > 0 ? `${r.weatherTemperatureC.toFixed(0)}°` : '';
                        return (
                          <div
                            key={r.sessionId}
                            onClick={() => { const qp = new URLSearchParams(); qp.set('type', r.sessionType); qp.set('driver', driverId); navigate(`#/races/${r.championshipId}/${r.eventId}/${encodeURIComponent(r.categoryName)}?${qp.toString()}`); }}
                            className={`
                              hidden sm:grid ${SESSION_GRID} gap-3
                              px-4 py-3 cursor-pointer
                              hover:bg-bg-hover active:scale-[0.995]
                              transition-all group bg-bg-surface/50
                              ${si < group.results.length - 1 ? 'border-b border-border-subtle' : ''}
                              ${si === group.results.length - 1 && gi < eventGroups.length - 1 ? 'border-b border-border-subtle/50' : ''}
                            `}
                          >
                            <span className="flex items-center justify-center">
                              <span className="w-1 h-1 rounded-full bg-border-default"></span>
                            </span>
                            <span>
                              <SessionTypeBadge sessionType={r.sessionType} />
                            </span>
                            <span className="text-text-muted text-xs truncate self-center">{r.categoryName}</span>
                            <span className="text-center self-center">
                              {r.finishPosition > 0 ? (
                                <PositionBadge position={r.finishPosition} size="sm" />
                              ) : (
                                <span className="text-text-muted">–</span>
                              )}
                            </span>
                            <span className="text-right font-mono text-sm text-text-primary self-center">
                              {formatLapTime(r.bestLapMs)}
                            </span>
                            <span className="text-right text-text-secondary text-xs whitespace-nowrap self-center">
                              {icon ? `${wTemp} ${icon}` : ''}
                            </span>
                            <span className="text-center self-center">
                              <StatusBadge status={r.status} />
                            </span>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Event parent row (mobile) */}
                  <div
                    onClick={() => toggleEvent(group.key)}
                    className={`sm:hidden px-4 py-3.5 cursor-pointer hover:bg-bg-hover
                      transition-colors active:scale-[0.998] select-none
                      ${!isExpanded && gi < eventGroups.length - 1 ? 'border-b border-border-subtle/50' : ''}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <ChevronRight
                        size={14}
                        className={`text-text-muted transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-semibold text-text-primary truncate">{group.trackName}</span>
                            <span className="inline-block px-2 py-0.5 bg-white/[0.06] border border-border-subtle rounded-full text-[0.65rem] font-medium text-text-secondary tabular-nums shrink-0">
                              {group.year}
                            </span>
                          </div>
                          <span className="text-[0.7rem] text-text-muted shrink-0">{sessionCountLabel}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[0.7rem] text-text-muted font-mono">{group.dateStart}</span>
                          {group.championshipName && (
                            <>
                              <span className="text-[0.7rem] text-text-secondary">·</span>
                              <span className="text-[0.7rem] text-text-secondary truncate">{group.championshipName}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded child session rows (mobile) */}
                  {isExpanded && group.results.map((r, si) => {
                    const icon = r.weatherCondition ? getWeatherEmoji(r.weatherCondition) : null;
                    const wTemp = r.weatherTemperatureC != null && r.weatherTemperatureC > 0 ? `${r.weatherTemperatureC.toFixed(0)}°` : '';
                    return (
                      <button
                        key={r.sessionId}
                        onClick={() => { const qp = new URLSearchParams(); qp.set('type', r.sessionType); qp.set('driver', driverId); navigate(`#/races/${r.championshipId}/${r.eventId}/${encodeURIComponent(r.categoryName)}?${qp.toString()}`); }}
                        className={`sm:hidden w-full text-left pl-10 pr-4 py-3 hover:bg-bg-hover
                          transition-colors group border-t border-border-subtle bg-bg-surface/50
                          ${si === group.results.length - 1 && gi < eventGroups.length - 1 ? 'border-b border-border-subtle/50' : ''}`}
                      >
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="w-1 h-1 rounded-full bg-border-default shrink-0"></span>
                              <SessionTypeBadge sessionType={r.sessionType} />
                              <span className="text-[0.7rem] text-text-muted truncate">{r.categoryName}</span>
                            </div>
                            {r.finishPosition > 0 && (
                              <PositionBadge position={r.finishPosition} size="sm" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap pl-3">
                            <span className="font-mono text-[0.7rem] text-text-primary">{formatLapTime(r.bestLapMs)}</span>
                            {icon && <span className="text-[0.7rem] text-text-secondary">{wTemp} {icon}</span>}
                            <StatusBadge status={r.status} />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </>
      )}
    </>
  );
}
